import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Navigate } from 'react-router-dom';
import { Globe, FileCheck, Users, BookOpen, BarChart2, Cpu, Shield, FolderTree, FileSearch, Lock, Building2, Database, UserX, ArrowRightLeft, Rocket, AlertCircle, Loader2, UserCog, ChevronLeft, ChevronRight, Settings, DollarSign } from 'lucide-react';
import { useAuth, API } from '../App';
import Header from '../components/layout/Header';
import { useWorkspace, WorkspaceSelector } from './enterprise/useWorkspace';
import DomainsSection from './enterprise/DomainsSection';
import ContentReviewSection from './enterprise/ContentReviewSection';
import PersonaLibrary from './enterprise/PersonaLibrary';
import PlaybookLibrary from './enterprise/PlaybookLibrary';
import ObservatorySection from './enterprise/ObservatorySection';
import ModelGovernance from './enterprise/ModelGovernance';
import SecuritySection from './enterprise/SecuritySection';
import CategoryManager from './enterprise/CategoryManager';
import AuditSection from '../components/enterprise/AuditSection';
import PrivacyComplianceSection from '../components/enterprise/PrivacyComplianceSection';
import DepartmentSection from '../components/enterprise/DepartmentSection';
import DataGovernanceSection from '../components/enterprise/DataGovernanceSection';
import SuspensionSection from '../components/enterprise/SuspensionSection';
import MigrationWizard from '../components/enterprise/MigrationWizard';
import OnboardingWizard from '../components/enterprise/OnboardingWizard';
import UsersSection from '../components/enterprise/UsersSection';
import MultiProviderKeys from '../components/enterprise/MultiProviderKeys';
import UsageReporting from '../components/enterprise/UsageReporting';

const TABS = [
  { id: 'domains', label: 'Domains', icon: Globe },
  { id: 'users', label: 'Users', icon: UserCog },
  { id: 'content', label: 'Content Review', icon: FileCheck },
  { id: 'personas', label: 'Personas', icon: Users },
  { id: 'playbooks', label: 'Playbooks', icon: BookOpen },
  { id: 'categories', label: 'Categories', icon: FolderTree },
  { id: 'observatory', label: 'Observatory', icon: BarChart2 },
  { id: 'models', label: 'Model Gov', icon: Cpu },
  { id: 'security', label: 'Security', icon: Shield },
  { id: 'audit', label: 'Audit', icon: FileSearch },
  { id: 'privacy', label: 'Privacy', icon: Lock },
  { id: 'departments', label: 'Departments', icon: Building2 },
  { id: 'governance', label: 'Governance', icon: Database },
  { id: 'suspension', label: 'Suspension', icon: UserX },
  { id: 'migration', label: 'Migration', icon: ArrowRightLeft },
  { id: 'onboarding', label: 'Onboarding', icon: Rocket },
  { id: 'providers', label: 'Providers', icon: Settings },
  { id: 'usage', label: 'Usage & Billing', icon: DollarSign },
];

export default function EnterpriseAdmin() {
  const { user } = useAuth();
  const { workspaces, activeWsId, selectWorkspace, loading: wsLoading } = useWorkspace();
  const [tab, setTab] = useState('domains');
  const tabBarRef = useRef(null);

  if (!user || (user.role !== 'enterprise' && user.role !== 'super_admin' && user.role !== 'admin')) {
    return <Navigate to="/dashboard" replace />;
  }

  if (wsLoading) {
    return (
      <div className="min-h-screen bg-[#0F0F0F] text-[#F5F5F5]">
        <Header />
        <div className="flex items-center justify-center py-20">
          <div className="w-8 h-8 border-2 border-[#FF4D00] border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  if (workspaces.length === 0) {
    return <AutoCreateWorkspace user={user} />;
  }

  return (
    <div className="min-h-screen bg-[#0F0F0F] text-[#F5F5F5]">
      <Header />
      <div className="max-w-6xl mx-auto px-4 py-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-6">
          <h1 className="text-2xl font-bold font-['Barlow_Condensed']">Enterprise Admin</h1>
          <WorkspaceSelector workspaces={workspaces} activeWsId={activeWsId} onSelect={selectWorkspace} />
        </div>

        {/* #514: Persistent onboarding progress indicator */}
        <OnboardingProgressBar workspaceId={activeWsId} onNavigate={() => setTab('onboarding')} />

        {/* #477: Tab bar with ALWAYS-VISIBLE chevrons — no scroll state detection */}
        <div className="flex items-start gap-0 mb-6">
          <button onClick={() => tabBarRef.current?.scrollBy({ left: -200, behavior: 'smooth' })}
            className="flex-shrink-0 w-8 min-h-[44px] flex items-center justify-center bg-[#1A1A1A] border border-[#2A2A2A] text-[#A0A0A0] hover:text-[#F5F1E8] hover:border-[#FF4D00] transition-colors"
            aria-label="Scroll tabs left">
            <ChevronLeft className="w-4 h-4" />
          </button>
          <div ref={tabBarRef} className="flex gap-1 flex-1 min-w-0" style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
            {TABS.map(t => {
              const Icon = t.icon;
              return (
                <button key={t.id} onClick={() => setTab(t.id)}
                  className={`flex items-center gap-1.5 px-3 py-2 text-xs font-mono whitespace-nowrap flex-shrink-0 min-h-[44px] transition-colors ${
                    tab === t.id
                      ? 'bg-[#FF4D00] text-black'
                      : 'bg-[#1A1A1A] border border-[#2A2A2A] text-[#A0A0A0] hover:text-[#F5F5F5] hover:border-[#FF4D00]/30'
                  }`}>
                  <Icon className="w-3.5 h-3.5" />
                  {t.label}
                </button>
              );
            })}
          </div>
          <button onClick={() => tabBarRef.current?.scrollBy({ left: 200, behavior: 'smooth' })}
            className="flex-shrink-0 w-8 min-h-[44px] flex items-center justify-center bg-[#1A1A1A] border border-[#2A2A2A] text-[#A0A0A0] hover:text-[#F5F1E8] hover:border-[#FF4D00] transition-colors"
            aria-label="Scroll tabs right">
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>

        <div className="bg-[#1A1A1A] border border-[#2A2A2A] p-4 md:p-6">
          {tab === 'domains' && <DomainsSection wsId={activeWsId} />}
          {tab === 'users' && <UsersSection workspaceId={activeWsId} />}
          {tab === 'content' && <ContentReviewSection wsId={activeWsId} />}
          {tab === 'personas' && <PersonaLibrary wsId={activeWsId} />}
          {tab === 'playbooks' && <PlaybookLibrary wsId={activeWsId} />}
          {tab === 'categories' && <CategoryManager wsId={activeWsId} scope="enterprise" />}
          {tab === 'observatory' && <ObservatorySection wsId={activeWsId} />}
          {tab === 'models' && <ModelGovernance wsId={activeWsId} />}
          {tab === 'security' && <SecuritySection wsId={activeWsId} />}
          {tab === 'audit' && <AuditSection workspaceId={activeWsId} />}
          {tab === 'privacy' && <PrivacyComplianceSection workspaceId={activeWsId} />}
          {tab === 'departments' && <DepartmentSection workspaceId={activeWsId} />}
          {tab === 'governance' && <DataGovernanceSection workspaceId={activeWsId} />}
          {tab === 'suspension' && <SuspensionSection workspaceId={activeWsId} />}
          {tab === 'migration' && <MigrationWizard workspaceId={activeWsId} />}
          {tab === 'onboarding' && <OnboardingWizard workspaceId={activeWsId} />}
          {tab === 'providers' && <MultiProviderKeys workspaceId={activeWsId} onNavigateTab={setTab} />}
          {tab === 'usage' && <UsageReporting workspaceId={activeWsId} />}
        </div>
      </div>
    </div>
  );
}

function OnboardingProgressBar({ workspaceId, onNavigate }) {
  const [status, setStatus] = useState(null); // { completed, total }

  const fetchProgress = useCallback(async () => {
    try {
      const r = await API.get(`/api/workspaces/${workspaceId}/onboarding`);
      const steps = r.data.steps || [];
      const completed = steps.filter(s => s.status === 'completed' || s.status === 'skipped').length;
      setStatus({ completed, total: steps.length });
    } catch {
      setStatus(null);
    }
  }, [workspaceId]);

  useEffect(() => { fetchProgress(); }, [fetchProgress]);

  // Don't show if loading, no steps, or all complete
  if (!status || status.total === 0 || status.completed >= status.total) return null;

  const pct = Math.round((status.completed / status.total) * 100);

  return (
    <button
      onClick={onNavigate}
      className="w-full mb-4 bg-[#1E1E1E] border border-[#2A2A2A] rounded-lg px-4 py-3 flex items-center gap-4 hover:border-[#FF4D00]/50 transition-colors group cursor-pointer text-left"
    >
      <Rocket className="w-5 h-5 text-[#FF4D00] flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-sm font-bold font-['Barlow_Condensed'] uppercase tracking-wide text-[#F5F1E8]">
            Enterprise Setup: {status.completed}/{status.total} complete
          </span>
          <span className="text-xs font-mono text-[#A0A0A0] group-hover:text-[#FF4D00] transition-colors">
            Continue setup →
          </span>
        </div>
        <div className="h-1.5 bg-[#0A0A0A] rounded-full overflow-hidden">
          <div
            className="h-full bg-[#FF4D00] rounded-full transition-all duration-500"
            style={{ width: `${pct}%` }}
          />
        </div>
      </div>
    </button>
  );
}

function AutoCreateWorkspace({ user }) {
  const [status, setStatus] = useState('creating'); // creating | error
  const [error, setError] = useState('');

  const autoCreate = useCallback(async () => {
    try {
      await API.post('/api/workspaces/auto-create-enterprise');
      // Reload to pick up the new workspace
      window.location.reload();
    } catch (e) {
      setStatus('error');
      setError(e.response?.data?.detail || 'Failed to create workspace. Please contact support.');
    }
  }, []);

  useEffect(() => { autoCreate(); }, [autoCreate]);

  return (
    <div className="min-h-screen bg-[#0F0F0F] text-[#F5F5F5]">
      <Header />
      <div className="max-w-md mx-auto px-4 py-20 text-center">
        {status === 'creating' ? (
          <>
            <Loader2 className="w-10 h-10 mx-auto mb-4 text-[#FF4D00] animate-spin" />
            <h1 className="text-xl font-bold font-['Barlow_Condensed'] mb-2">Setting Up Your Workspace</h1>
            <p className="text-sm text-[#A0A0A0]">
              Creating your enterprise workspace from <span className="text-[#00D4FF] font-mono">{user?.email?.split('@')[1]}</span>...
            </p>
          </>
        ) : (
          <>
            <AlertCircle className="w-10 h-10 mx-auto mb-4 text-[#A61E17]" />
            <h1 className="text-xl font-bold font-['Barlow_Condensed'] mb-2">Workspace Setup Failed</h1>
            <p className="text-sm text-[#A0A0A0] mb-4">{error}</p>
            <p className="text-xs text-[#A0A0A0]">Please contact support at <span className="text-[#FF4D00]">support@getfray.ai</span></p>
            <button onClick={() => { setStatus('creating'); autoCreate(); }}
              className="mt-4 px-4 py-2 bg-[#FF4D00] text-[#0A0A0A] font-bold text-sm rounded hover:bg-[#E63E00]">
              Retry
            </button>
          </>
        )}
      </div>
    </div>
  );
}
